import { API_BASE_URL, apiRequest } from '../client'

export const imagesApi = {
  /**
   * Uploads admin imagery (restaurant thumbnail/header) to cloud-service.
   *
   * Returns a `media/<id>` reference to store rather than a URL: a URL baked into a row is what
   * made leaving Cloudflare expensive, and the delivered URL depends on the environment and on the
   * width being asked for. `url` is the same image ready to display, for the preview.
   */
  uploadAdminImage: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return apiRequest<{ ref: string; url: string; byteSize: number }>(
      `/api/v0/admin/images/upload`,
      { method: 'POST', body: form },
    )
  },

  /**
   * Where a stored media reference can be viewed.
   *
   * Absolute URLs are the historical form and are used as they are. A `media/<id>` reference
   * resolves to the original, which is heavier than the delivered image but needs no signing — fine
   * for a 64px preview in an admin form.
   */
  mediaPreviewUrl: (value?: string) => {
    if (!value) return undefined
    if (/^https?:\/\//i.test(value)) return value
    return `${API_BASE_URL}/api/v1/${value}`
  },

  getUploadUrl: (
    sku: string,
    payload?: { filename?: string; contentType?: string },
  ) =>
    apiRequest<{ uploadURL: string; imageId: string; expiresAt: string }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/images/upload-url`,
      {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      },
    ),

  list: (sku: string) =>
    apiRequest<{
      items: Array<{
        id: string
        sortOrder: number
        alt?: string
        url: string
        thumbUrl: string
      }>
    }>(`/api/v0/admin/products/${encodeURIComponent(sku)}/images`),

  attach: (
    sku: string,
    payload: { imageId: string; sortOrder?: number; alt?: string },
  ) =>
    apiRequest<{ ok: boolean }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/images`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    ),

  update: (
    sku: string,
    imageId: string,
    payload: { sortOrder?: number; alt?: string },
  ) =>
    apiRequest<{ ok: boolean }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/images/${encodeURIComponent(imageId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    ),

  reorder: (
    sku: string,
    items: Array<{ imageId: string; sortOrder: number }>,
  ) =>
    apiRequest<{ ok: boolean; updated: number }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/images/reorder`,
      {
        method: 'PUT',
        body: JSON.stringify({ items }),
      },
    ),

  remove: (sku: string, imageId: string) =>
    apiRequest<{ ok: boolean }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/images/${encodeURIComponent(imageId)}`,
      {
        method: 'DELETE',
      },
    ),

  /** Product images still go through the image store's direct upload. */
  directUpload: async (uploadURL: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    const res = await fetch(uploadURL, {
      method: 'POST',
      body: form,
    })
    if (!res.ok) {
      throw new Error('image upload failed')
    }
    return res.json()
  },
}
